import { faUsers } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Button } from "primereact/button";
import { InputText } from "primereact/inputtext";
import { Toast } from "primereact/toast";
import React from "react";
import { FanService } from "src/app/services/Index";
import "./AdminFans.css";
import "../ticketing/AdminTicketing.css";

type Props = {};
type State = {
	available: boolean;
	isLoading: boolean;
	fans: any[];
	total: number;
	totalFans: number;
	activeFans: number;
	search: string;
	page: number;
	pageSize: number;
};

const money = (pence: any) =>
	`£${((Number(pence) || 0) / 100).toLocaleString("en-GB", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const date = (value: any) => (value ? new Date(value).toLocaleDateString("en-GB") : "—");

/**
 * A club's supporters.
 *
 * Reads through ClubFans, so this only ever shows this club's own fans —
 * Fan itself is global across Pitchbase.
 *
 * Guests appear alongside registered fans because a guest checkout is still
 * someone who gave the club money; leaving them out would make this page
 * disagree with the orders it came from.
 */
export class AdminFans extends React.Component<Props, State> {
	toast: any;
	searchTimer: any;

	constructor(props: Props) {
		super(props);
		this.state = {
			available: true, isLoading: true, fans: [], total: 0,
			totalFans: 0, activeFans: 0, search: "", page: 1, pageSize: 20
		};
	}

	componentDidMount() {
		this.load();
	}

	async load() {
		this.setState({ isLoading: true });
		try {
			const res = await FanService.list(this.state.search, this.state.page, this.state.pageSize);
			if (!res.ok || res.status === 204) {
				this.setState({ available: false, isLoading: false });
				return;
			}
			const body = await res.json();
			if (!body?.available) {
				this.setState({ available: false, isLoading: false });
				return;
			}
			this.setState({
				available: true,
				fans: Array.isArray(body.fans) ? body.fans : [],
				total: body.total || 0,
				totalFans: body.totalFans || 0,
				activeFans: body.activeFans || 0,
				isLoading: false
			});
		} catch {
			this.setState({ available: false, isLoading: false });
		}
	}

	/** Typing shouldn't fire a request per keystroke. */
	onSearch(value: string) {
		this.setState({ search: value, page: 1 });
		clearTimeout(this.searchTimer);
		this.searchTimer = setTimeout(() => this.load(), 350);
	}

	async togglePriority(fan: any) {
		if (!fan.clubFanId) return;
		const next = !fan.isPriority;

		// Optimistic: a checkbox that waits on a round trip feels broken.
		this.setState({
			fans: this.state.fans.map((f) => (f.clubFanId === fan.clubFanId ? { ...f, isPriority: next } : f))
		});

		try {
			const res = await FanService.setPriority(fan.clubFanId, next);
			if (!res.ok) throw new Error();
		} catch {
			this.setState({
				fans: this.state.fans.map((f) => (f.clubFanId === fan.clubFanId ? { ...f, isPriority: !next } : f))
			});
			this.toast?.show({ closable: false, detail: "That didn't save. Please try again.", life: 4000, severity: "error", summary: "Priority" });
		}
	}

	pages() {
		return Math.max(1, Math.ceil(this.state.total / this.state.pageSize));
	}

	go(page: number) {
		const target = Math.min(Math.max(1, page), this.pages());
		if (target === this.state.page) return;
		this.setState({ page: target }, () => this.load());
	}

	render() {
		const { available, isLoading, fans, total, totalFans, activeFans, page } = this.state;

		if (!isLoading && !available) {
			return (
				<div style={{ padding: 20 }}>
					<h2 style={{ marginBottom: 20 }}>Fans</h2>
					<div className="fn-empty">
						<FontAwesomeIcon icon={faUsers} />
						<h3>Fans aren&rsquo;t available yet</h3>
						<p>The supporter tables haven&rsquo;t been created on this environment.</p>
					</div>
				</div>
			);
		}

		return (
			<div style={{ padding: 20 }}>
				<Toast ref={(el) => (this.toast = el)} />
				<h2 style={{ marginBottom: 18 }}>Fans</h2>

				<div className="fn-stats">
					<div className="fn-stat">
						<h4>Fans</h4>
						<span className="fn-stat-sub">Total number of club followers</span>
						<div className="fn-stat-value">
							<span className="fn-stat-icon"><FontAwesomeIcon icon={faUsers} /></span>
							{totalFans.toLocaleString("en-GB")}
						</div>
					</div>
					<div className="fn-stat">
						<h4>Active fans</h4>
						<span className="fn-stat-sub">Fans with a transaction in the last 12 months</span>
						<div className="fn-stat-value">
							<span className="fn-stat-icon"><FontAwesomeIcon icon={faUsers} /></span>
							{activeFans.toLocaleString("en-GB")}
						</div>
					</div>
				</div>

				<div className="fn-toolbar">
					<span className="p-input-icon-left fn-search">
						<i className="pi pi-search" />
						<InputText onChange={(e) => this.onSearch(e.target.value)} placeholder="Search fan name or email" value={this.state.search} />
					</span>
					<div className="fn-toolbar-right">
						<Button className="p-button-sm" label="Download fans list" onClick={() => window.open(FanService.exportUrl(), "_blank")} />
						<Button className="p-button-sm" label="Download marketing list" onClick={() => window.open(FanService.exportMarketingUrl(), "_blank")} />
					</div>
				</div>

				{isLoading && <p style={{ color: "var(--pb-text-muted)" }}>Loading&hellip;</p>}

				{!isLoading && fans.length === 0 &&
					<div className="fn-empty">
						<FontAwesomeIcon icon={faUsers} />
						<h3>No fans yet</h3>
						<p>Supporters appear here once they follow the club or buy a ticket.</p>
					</div>}

				{!isLoading && fans.length > 0 && <div className="tk-table-wrap">
					<table className="tk-table">
						<thead>
							<tr>
								<th>Name and email</th>
								<th>Priority</th>
								<th>Fan since</th>
								<th className="num">Orders</th>
								<th className="num">Total spend</th>
								<th>Most recent order</th>
								<th></th>
							</tr>
						</thead>
						<tbody>
							{fans.map((fan: any, i: number) => (
								<tr key={`fan-${fan.clubFanId || fan.email || i}`}>
									<td>
										<div className="fn-who">
											{fan.name && <span className="fn-name">{fan.name}</span>}
											<span className="fn-email">{fan.email}</span>
										</div>
									</td>
									<td>
										{/* A guest has no relationship to flag — there's nothing to attach it to. */}
										{fan.isGuest
											? <span className="fn-na" title="Guests can't be flagged until they register">&mdash;</span>
											: <input checked={!!fan.isPriority} onChange={() => this.togglePriority(fan)} type="checkbox" />}
									</td>
									<td>{fan.isGuest ? <span className="fn-pill">Guest</span> : date(fan.fanSince)}</td>
									<td className="num">{fan.orderCount ?? 0}</td>
									<td className="num">{money(fan.totalSpendPence)}</td>
									<td>{date(fan.mostRecentOrder)}</td>
									<td><Button className="p-button-sm" disabled label="View" title="Fan profile is not built yet" /></td>
								</tr>
							))}
						</tbody>
					</table>
				</div>}

				{!isLoading && total > 0 && <div className="fn-pager">
					<Button className="p-button-sm p-button-outlined" disabled={page <= 1} label="First" onClick={() => this.go(1)} />
					<Button className="p-button-sm p-button-outlined" disabled={page <= 1} label="Prev" onClick={() => this.go(page - 1)} />
					<span className="fn-pageno">{page} / {this.pages()}</span>
					<Button className="p-button-sm p-button-outlined" disabled={page >= this.pages()} label="Next" onClick={() => this.go(page + 1)} />
					<Button className="p-button-sm p-button-outlined" disabled={page >= this.pages()} label="Last" onClick={() => this.go(this.pages())} />
					<span className="fn-count">
						{(page - 1) * this.state.pageSize + 1} &ndash; {Math.min(page * this.state.pageSize, total)} of {total.toLocaleString("en-GB")}
					</span>
				</div>}
			</div>
		);
	}
}
